import React from 'react';
import { useReactions } from '../hooks/useReactions';
import { useStreamAuth } from '../hooks/useStreamAuth';
import './PlayerReactionButtons.css';

/**
 * Player-specific reaction buttons component
 * Displays three separate buttons in the center of the video player when paused:
 * - Left: Like (simple thumbs up)
 * - Center: Double Like (enhanced thumbs up with heart)
 * - Right: Dislike (thumbs down)
 */
const PlayerReactionButtons = ({ 
  channelId, 
  initialReaction = null,
  className = '',
  onReactionChange = null, 
}) => {
  const { isAuthenticated } = useStreamAuth();
  
  // Use the custom reactions hook
  const { 
    currentReaction, 
    isLoading, 
    error, 
    setReaction,
  } = useReactions(channelId, initialReaction);

  // Don't render anything if user is not authenticated or no channelId
  if (!isAuthenticated || !channelId) {
    return null;
  }

  /**
   * Handle reaction button click
   * @param {string} action - The reaction type: 'like', 'dislike', 'double_like'
   */
  const handleReaction = async (action) => {
    if (isLoading) {return;}

    console.log('🎬 PlayerReactionButtons: handleReaction called:', { 
      action, 
      currentReaction, 
      willRemove: currentReaction === action, 
    });

    // Calculate what the new reaction will be (same logic as useReactions)
    const willRemove = currentReaction === action;
    const newReaction = willRemove ? null : action;

    const success = await setReaction(action);
    
    console.log('🎬 PlayerReactionButtons: setReaction result:', success);
    
    if (success) {
      // Notify parent component of reaction change with the actual new value
      if (onReactionChange) {
        onReactionChange(newReaction);
      }
    }
  };

  // Debug logging (temporarily disabled)
  // console.log('🎯 PlayerReactionButtons render:', { 
  //   isAuthenticated, 
  //   channelId, 
  //   currentReaction, 
  //   isLoading, 
  //   error 
  // });

  return (
    <div className={`player-reaction-buttons ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="player-reaction-error">
          Failed to update reaction
        </div>
      )}

      {/* Three Reaction Buttons */}
      <div className="player-reaction-buttons-container">
        {/* Like Button (Left) */}
        <button
          className={`player-reaction-button player-reaction-button--like ${
            currentReaction === 'like' ? 'player-reaction-button--active' : ''
          }`}
          onClick={() => handleReaction('like')}
          disabled={isLoading}
          title={currentReaction === 'like' ? 'Remove like' : 'I like this'}
          aria-label={currentReaction === 'like' ? 'Remove like' : 'I like this'}
        >
          <div className="player-reaction-icon-container">
            {isLoading && currentReaction === 'like' ? (
              <div className="player-reaction-loading-spinner" />
            ) : (
              <ThumbsUpIcon filled={currentReaction === 'like'} />
            )}
          </div>
          <span className="player-reaction-label">I like this</span>
        </button>

        {/* Double Like Button (Center) */}
        <button
          className={`player-reaction-button player-reaction-button--double-like ${
            currentReaction === 'double_like' ? 'player-reaction-button--active' : ''
          }`}
          onClick={() => handleReaction('double_like')}
          disabled={isLoading}
          title={currentReaction === 'double_like' ? 'Remove love' : 'I really like this'}
          aria-label={currentReaction === 'double_like' ? 'Remove love' : 'I really like this'}
        >
          <div className="player-reaction-icon-container">
            {isLoading && currentReaction === 'double_like' ? (
              <div className="player-reaction-loading-spinner" />
            ) : (
              <ThumbsUpIcon filled={currentReaction === 'double_like'} enhanced={true} />
            )}
          </div>
          <span className="player-reaction-label">I really like this</span>
        </button>

        {/* Dislike Button (Right) */}
        <button
          className={`player-reaction-button player-reaction-button--dislike ${
            currentReaction === 'dislike' ? 'player-reaction-button--active' : ''
          }`}
          onClick={() => handleReaction('dislike')}
          disabled={isLoading}
          title={currentReaction === 'dislike' ? 'Remove dislike' : 'I dislike this'}
          aria-label={currentReaction === 'dislike' ? 'Remove dislike' : 'I dislike this'}
        >
          <div className="player-reaction-icon-container">
            {isLoading && currentReaction === 'dislike' ? (
              <div className="player-reaction-loading-spinner" />
            ) : (
              <ThumbsDownIcon filled={currentReaction === 'dislike'} />
            )}
          </div>
          <span className="player-reaction-label">I dislike this</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Thumbs Up Icon Component
 */
const ThumbsUpIcon = ({ filled = false, enhanced = false }) => (
  <svg 
    width="32" 
    height="32" 
    viewBox="0 0 24 24" 
    fill={filled ? "currentColor" : "none"} 
    stroke="currentColor" 
    strokeWidth="2"
    className={`player-reaction-icon ${enhanced ? 'player-reaction-icon--enhanced' : ''}`}
  >
    <path d="M7 10v12"/>
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/>
    {enhanced && (
      <>
        <circle cx="18" cy="6" r="2" fill="currentColor" opacity="0.8"/>
        <path d="M16 4l2 2 2-2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </>
    )}
  </svg>
);

/**
 * Thumbs Down Icon Component
 */
const ThumbsDownIcon = ({ filled = false }) => (
  <svg 
    width="32" 
    height="32" 
    viewBox="0 0 24 24" 
    fill={filled ? "currentColor" : "none"} 
    stroke="currentColor" 
    strokeWidth="2"
    className="player-reaction-icon"
  >
    <path d="M17 14V2"/>
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/>
  </svg>
);

export default PlayerReactionButtons;
